import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardHeader, Button, Input, Select, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';
import { ImageUploadInput } from '@/components/ImageUploadInput';

interface Category {
  id: string;
  name: string;
  slug: string;
}

const TYPE_OPTIONS = [
  { value: 'DTF_BY_SIZE', label: 'DTF by Size' },
  { value: 'GANGSHEET', label: 'Gang Sheet' },
  { value: 'UV_STICKER', label: 'UV Sticker' },
  { value: 'APPAREL', label: 'Apparel' },
  { value: 'DRINKWARE', label: 'Drinkware' },
  { value: 'ACCESSORIES', label: 'Accessories' },
  { value: 'HOME_DECOR', label: 'Home Decor' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'CUSTOM', label: 'Custom' },
];

const DESIGNER_MODE_OPTIONS = [
  { value: 'NONE', label: 'No Designer' },
  { value: 'DTF_BY_SIZE', label: 'DTF by Size' },
  { value: 'GANGSHEET', label: 'Gang Sheet' },
  { value: 'APPAREL', label: 'Apparel (multi-side)' },
  { value: 'PRODUCT_MOCKUP', label: 'Product Mockup' },
];

const DESIGN_CONTEXT_OPTIONS = [
  { value: 'STOREFRONT', label: 'Storefront' },
  { value: 'PRODUCT', label: 'Product' },
];

const TECHNIQUE_OPTIONS = [
  { value: 'DTG', label: 'DTG' },
  { value: 'EMBROIDERY', label: 'Embroidery' },
  { value: 'SCREEN_PRINT', label: 'Screen Print' },
  { value: 'SUBLIMATION', label: 'Sublimation' },
  { value: 'DTF', label: 'DTF' },
  { value: 'VINYL', label: 'Vinyl' },
];

export function CatalogCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [type, setType] = useState('DTF_BY_SIZE');
  const [designerMode, setDesignerMode] = useState('NONE');
  const [designContext, setDesignContext] = useState('STOREFRONT');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [brand, setBrand] = useState('');
  const [modelName, setModelName] = useState('');
  const [material, setMaterial] = useState('');
  const [fabricWeight, setFabricWeight] = useState('');
  const [originCountry, setOriginCountry] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [techniques, setTechniques] = useState<string[]>([]);
  const [avgFulfillmentDays, setAvgFulfillmentDays] = useState('');
  const [isDiscontinued, setIsDiscontinued] = useState(false);

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery<{ data: Category[] }>({
    queryKey: ['catalog-categories'],
    queryFn: () => api.get<{ data: Category[] }>('/api/v1/admin/catalog/categories'),
  });

  const categories = categoriesData?.data ?? [];

  const categoryOptions = [
    { value: '', label: 'No Category' },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ];

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ data: { id: string } }>('/api/v1/admin/catalog/templates', body),
    onSuccess: (res) => {
      navigate(`/catalog/${res.data.id}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name,
      type,
      designerMode,
      designContext,
      ...(categoryId ? { categoryId } : {}),
      ...(description ? { description } : {}),
      ...(brand ? { brand } : {}),
      ...(modelName ? { modelName } : {}),
      ...(material ? { material } : {}),
      ...(fabricWeight ? { fabricWeight } : {}),
      ...(originCountry ? { originCountry } : {}),
      ...(thumbnailUrl ? { thumbnailUrl } : {}),
      ...(techniques.length > 0 ? { techniques } : {}),
      ...(avgFulfillmentDays ? { avgFulfillmentDays: parseInt(avgFulfillmentDays) } : {}),
      ...(isDiscontinued ? { isDiscontinued } : {}),
    });
  };

  return (
    <div>
      <Card>
        <CardHeader
          title="Create Template"
          description="Create a new Unified Product Template."
        />

        <form onSubmit={handleSubmit} className="space-y-5 px-6 pb-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Name <span className="text-danger-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. DTF Transfer by Size"
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Type <span className="text-danger-500">*</span>
              </label>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                options={TYPE_OPTIONS}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Designer Mode <span className="text-danger-500">*</span>
              </label>
              <Select
                value={designerMode}
                onChange={(e) => setDesignerMode(e.target.value)}
                options={DESIGNER_MODE_OPTIONS}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Design Context
              </label>
              <Select
                value={designContext}
                onChange={(e) => setDesignContext(e.target.value)}
                options={DESIGN_CONTEXT_OPTIONS}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Category</label>
              {categoriesLoading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-text-tertiary">
                  <Spinner size="sm" /> Loading categories...
                </div>
              ) : (
                <Select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  options={categoryOptions}
                />
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Brand</label>
              <Input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g. Bella+Canvas"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Model Name</label>
              <Input
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="e.g. 3001"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Material</label>
              <Input
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                placeholder="e.g. 100% combed ring-spun cotton"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Fabric Weight</label>
              <Input
                value={fabricWeight}
                onChange={(e) => setFabricWeight(e.target.value)}
                placeholder="e.g. 4.2 oz/yd²"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Origin Country</label>
              <Input
                value={originCountry}
                onChange={(e) => setOriginCountry(e.target.value)}
                placeholder="e.g. Nicaragua"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Avg. Fulfillment Days</label>
              <Input
                type="number"
                min="0"
                value={avgFulfillmentDays}
                onChange={(e) => setAvgFulfillmentDays(e.target.value)}
                placeholder="e.g. 3"
              />
            </div>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <input
                  type="checkbox"
                  checked={isDiscontinued}
                  onChange={(e) => setIsDiscontinued(e.target.checked)}
                  className="rounded border-border"
                />
                Discontinued
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Techniques</label>
              <div className="flex flex-wrap gap-2">
                {TECHNIQUE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      techniques.includes(opt.value)
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-border bg-surface text-text-secondary hover:bg-surface-secondary'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={techniques.includes(opt.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTechniques((prev) => [...prev, opt.value]);
                        } else {
                          setTechniques((prev) => prev.filter((v) => v !== opt.value));
                        }
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text-primary">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Brief description of this template..."
              />
            </div>

            <div className="md:col-span-2">
              <ImageUploadInput
                label="Thumbnail Image"
                value={thumbnailUrl}
                directory="catalog/thumbnails"
                onChange={(url) => setThumbnailUrl(url)}
                previewSize="md"
              />
            </div>
          </div>

          {createMutation.isError && (
            <div className="rounded-md bg-danger-50 p-3 text-sm text-danger-700">
              {(createMutation.error as Error)?.message || 'Failed to create template.'}
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={!name || createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Template'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/catalog')}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
